import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Edit, Trash2, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/Contacts.css';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
  roles: string[];
  isActive: boolean;
  workspaces: string[];
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  roles: string[];
  isActive: boolean;
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState<string | null>(null);

  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    roles: [],
    isActive: true
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchUsers();
  }, [workspaceId]);

  const fetchUsers = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/users`);
      setUsers(response.data.users || []);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAddUser = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      roles: [],
      isActive: true
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.mobile || '',
      roles: user.roles,
      isActive: user.isActive
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.roles.length === 0) {
      errors.roles = 'At least one role must be selected';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAdd = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const response = await api.post(`/workspaces/${workspaceId}/users`, formData);
      toast.success('User added successfully');
      setShowAddModal(false);

      // Add the new user to the list without reloading
      setUsers(prevUsers => [...prevUsers, response.data.user]);
    } catch (err: any) {
      console.error('Error adding user:', err);
      toast.error(err.response?.data?.message || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!validateForm() || !selectedUser) return;

    try {
      setSaving(true);
      const response = await api.put(`/workspaces/${workspaceId}/users/${selectedUser._id}`, formData);
      toast.success('User updated successfully');
      setShowEditModal(false);

      // Update the user in the list without reloading
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u._id === selectedUser._id ? response.data.user : u
        )
      );
    } catch (err: any) {
      console.error('Error updating user:', err);
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await api.delete(`/workspaces/${workspaceId}/users/${selectedUser._id}`);
      toast.success('User removed from workspace successfully');
      setShowDeleteModal(false);

      // Remove the user from the list without reloading
      setUsers(prevUsers => prevUsers.filter(u => u._id !== selectedUser._id));
    } catch (err: any) {
      console.error('Error removing user from workspace:', err);
      toast.error(err.response?.data?.message || 'Failed to remove user from workspace');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    // Prevent multiple simultaneous reset requests
    if (resettingPasswordUserId === userId) {
      return;
    }

    try {
      setResettingPasswordUserId(userId);
      await api.post(`/workspaces/${workspaceId}/users/${userId}/reset-password`);
      toast.success('Password reset email sent');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const toggleRole = (role: string) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const toggleDisabled = async (user: User) => {
    // Prevent users from disabling themselves
    if (currentUser && currentUser._id === user._id && user.isActive) {
      toast.error('You cannot disable your own account');
      return;
    }

    // Prevent toggling if already in progress
    if (togglingUserId === user._id) {
      return;
    }

    try {
      setTogglingUserId(user._id);
      const response = await api.put(`/workspaces/${workspaceId}/users/${user._id}`, {
        isActive: !user.isActive
      });
      toast.success(`User ${user.isActive ? 'disabled' : 'enabled'} successfully`);

      // Update the user in the list without reloading
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u._id === user._id ? { ...u, isActive: !u.isActive } : u
        )
      );
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      toast.error(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setTogglingUserId(null);
    }
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Page Header */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Users</h1>
            <p className="builders-section-subtitle">
              Manage user accounts and permissions
            </p>
          </div>
          <button className="btn btn-primary btn-md" onClick={handleAddUser}>
            Add
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Active</th>
                    <th>Disabled</th>
                    <th>Reset Password</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user._id} style={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                    }}>
                      <td className="data-table-primary-cell">{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.roles.join(', ')}</td>
                      <td>{user.isActive ? 'Yes' : 'No'}</td>
                      <td>
                        {togglingUserId === user._id ? (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '24px'
                          }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid #D4CBC4',
                              borderTopColor: '#48182F',
                              borderRadius: '50%',
                              animation: 'spin 0.6s linear infinite'
                            }}></div>
                          </div>
                        ) : (
                          <label
                            className="switch"
                            title={currentUser?._id === user._id ? 'You cannot disable your own account' : ''}
                          >
                            <input
                              type="checkbox"
                              checked={!user.isActive}
                              onChange={() => toggleDisabled(user)}
                              disabled={currentUser?._id === user._id && user.isActive}
                            />
                            <span className={`slider round ${currentUser?._id === user._id && user.isActive ? 'disabled' : ''}`}></span>
                          </label>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleResetPassword(user._id)}
                          disabled={resettingPasswordUserId === user._id}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            backgroundColor: '#48182F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: resettingPasswordUserId === user._id ? 'not-allowed' : 'pointer',
                            opacity: resettingPasswordUserId === user._id ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {resettingPasswordUserId === user._id && (
                            <div style={{
                              width: '12px',
                              height: '12px',
                              border: '2px solid #FFFFFF',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.6s linear infinite'
                            }}></div>
                          )}
                          Reset Password
                        </button>
                      </td>
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center'
                        }}>
                          <button
                            onClick={() => handleEditUser(user)}
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Edit user"
                          >
                            <Edit size={18} color="#48182F" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(user)}
                            disabled={user.roles.includes('ADMINISTRATORS')}
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: user.roles.includes('ADMINISTRATORS') ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: user.roles.includes('ADMINISTRATORS') ? 0.4 : 1
                            }}
                            title={user.roles.includes('ADMINISTRATORS') ? 'Users with ADMINISTRATORS role cannot be removed' : 'Remove user from workspace'}
                          >
                            <Trash2 size={18} color="#DC2626" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal-content user-modal">
              <div className="modal-header">
                <h2>Add User</h2>
                <button className="modal-close" onClick={() => setShowAddModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body" style={{ padding: '32px' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">FIRST NAME *</label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.firstName ? 'error' : ''}`}
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Enter first name"
                    />
                    {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">LAST NAME *</label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.lastName ? 'error' : ''}`}
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">EMAIL *</label>
                    <input
                      type="email"
                      className={`form-input ${formErrors.email ? 'error' : ''}`}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email address"
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">MOBILE</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">ROLES *</label>
                  <div className="roles-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('ADMINISTRATORS')}
                        onChange={() => toggleRole('ADMINISTRATORS')}
                      />
                      <span>Administrators</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('USERS')}
                        onChange={() => toggleRole('USERS')}
                      />
                      <span>Users</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('MANAGER')}
                        onChange={() => toggleRole('MANAGER')}
                      />
                      <span>Manager</span>
                    </label>
                  </div>
                  {formErrors.roles && <span className="error-message">{formErrors.roles}</span>}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Is Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
                  {saving ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal-content user-modal">
              <div className="modal-header">
                <h2>Edit User</h2>
                <button className="modal-close" onClick={() => setShowEditModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body" style={{ padding: '32px' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">FIRST NAME *</label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.firstName ? 'error' : ''}`}
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Enter first name"
                    />
                    {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">LAST NAME *</label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.lastName ? 'error' : ''}`}
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">EMAIL *</label>
                    <input
                      type="email"
                      className={`form-input ${formErrors.email ? 'error' : ''}`}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email address"
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">MOBILE</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">ROLES *</label>
                  <div className="roles-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('ADMINISTRATORS')}
                        onChange={() => toggleRole('ADMINISTRATORS')}
                      />
                      <span>Administrators</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('USERS')}
                        onChange={() => toggleRole('USERS')}
                      />
                      <span>Users</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.roles.includes('MANAGER')}
                        onChange={() => toggleRole('MANAGER')}
                      />
                      <span>Manager</span>
                    </label>
                  </div>
                  {formErrors.roles && <span className="error-message">{formErrors.roles}</span>}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Is Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove User Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '480px' }}>
              <div className="modal-header">
                <h2>Remove User from Workspace</h2>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to remove <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> from this workspace?</p>
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#FEF3C7',
                  borderLeft: '4px solid #F59E0B',
                  borderRadius: '4px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400E' }}>
                    <strong>Note:</strong> This will only remove the user's access to this workspace. They can still:
                  </p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '14px', color: '#92400E' }}>
                    <li>Access other workspaces they're a member of</li>
                    <li>Create new workspaces</li>
                    <li>Log in to the application</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn"
                  style={{ backgroundColor: '#DC2626', color: 'white' }}
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Removing...' : 'Remove from Workspace'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSS for toggle switch */}
        <style>{`
          .switch {
            position: relative;
            display: inline-block;
            width: 48px;
            height: 24px;
          }

          .switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }

          .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .4s;
          }

          .slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .4s;
          }

          input:checked + .slider {
            background-color: #48182F;
          }

          input:checked + .slider:before {
            transform: translateX(24px);
          }

          .slider.round {
            border-radius: 24px;
          }

          .slider.round:before {
            border-radius: 50%;
          }

          .slider.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          input:disabled + .slider {
            cursor: not-allowed;
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-content {
            background: white;
            border-radius: 12px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid #E5E7EB;
          }

          .modal-header h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #48182F;
          }

          .modal-close {
            background: none;
            border: none;
            cursor: pointer;
            color: #6B7280;
            padding: 4px;
          }

          .modal-close:hover {
            color: #48182F;
          }

          .modal-body {
            padding: 24px;
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 24px;
            border-top: 1px solid #E5E7EB;
          }

          .error-message {
            color: #DC2626;
            font-size: 12px;
            margin-top: 4px;
            display: block;
          }

          .form-input.error {
            border-color: #DC2626;
          }

          .user-modal {
            max-width: 600px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          .form-group {
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
          }

          .form-label {
            font-size: 12px;
            font-weight: 600;
            color: #48182F;
            margin-bottom: 8px;
            letter-spacing: 0.5px;
          }

          .form-input {
            padding: 10px 12px;
            border: 1px solid #D1D5DB;
            border-radius: 6px;
            font-size: 14px;
            color: #48182F;
            transition: border-color 0.2s;
          }

          .form-input:focus {
            outline: none;
            border-color: #48182F;
          }

          .form-input::placeholder {
            color: #9CA3AF;
          }

          .roles-checkboxes {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding: 12px;
            background-color: #F9FAFB;
            border-radius: 6px;
            border: 1px solid #E5E7EB;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            cursor: pointer;
            font-size: 14px;
            color: #374151;
            user-select: none;
          }

          .checkbox-label:hover {
            color: #48182F;
          }

          .checkbox-input {
            width: 18px;
            height: 18px;
            margin-right: 10px;
            cursor: pointer;
            accent-color: #48182F;
          }

          .checkbox-label span {
            font-weight: 500;
          }

          .btn {
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            border: none;
            outline: none;
          }

          .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-primary {
            background-color: #48182F;
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background-color: #5a1f3c;
            box-shadow: 0 2px 8px rgba(72, 24, 47, 0.3);
          }

          .btn-secondary {
            background-color: #F3F4F6;
            color: #374151;
            border: 1px solid #D1D5DB;
          }

          .btn-secondary:hover:not(:disabled) {
            background-color: #E5E7EB;
            border-color: #9CA3AF;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </AppLayout>
  );
};

export default Users;
